import {
  ActivityMatrixGroup,
  ActivityMatrixModel,
  ActivityMatrixRow,
} from '../@types/service-activity.type';
import { ServiceActivityResponseDataType } from '../@types/service.type';
import {
  ACTIVITY_STATUS_COLUMNS,
  ActivityStatusField,
  UNGROUPED_CLIENT_LABEL,
} from '../constants/service-activity.constant';

type CountMap = Record<ActivityStatusField, number>;

const toCount = (value: unknown): number =>
  typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : 0;

export const emptyCounts = (): CountMap =>
  ACTIVITY_STATUS_COLUMNS.reduce((acc, column) => {
    acc[column.field] = 0;
    return acc;
  }, {} as CountMap);

const sumCounts = (target: CountMap, source: CountMap): void => {
  for (const column of ACTIVITY_STATUS_COLUMNS) target[column.field] += source[column.field];
};

const totalOf = (counts: CountMap): number =>
  ACTIVITY_STATUS_COLUMNS.reduce((sum, column) => sum + counts[column.field], 0);

/**
 * Leaf label for a row.
 *
 * `rowHeading` is typically `"<clientCode> <serviceTypeName>"` (e.g. `FSG
 * Install`). Under a `FSG` group header that repeats the client, so strip the
 * prefix. When the heading isn't client-prefixed the assumption is wrong for
 * this payload — fall back to the raw heading rather than showing a guess.
 */
export const deriveRowLabel = (row: ServiceActivityResponseDataType): string => {
  const heading = (row.rowHeading ?? '').trim();
  const serviceType = (row.serviceTypeName ?? '').trim();
  const clientCode = (row.clientCode ?? '').trim();

  if (heading && clientCode && heading.toLowerCase().startsWith(clientCode.toLowerCase())) {
    // Only strip on a word boundary. A bare `startsWith` turns client code "A"
    // + heading "Annual Maintenance" into "nnual Maintenance".
    const boundary = heading.charAt(clientCode.length);
    if (boundary === '' || /\s/.test(boundary)) {
      const stripped = heading.slice(clientCode.length).trim();
      if (stripped) return stripped;
    }
  }
  return heading || serviceType || UNGROUPED_CLIENT_LABEL;
};

/**
 * Intensity bucket for a cell, 0 (empty) to 4 (busiest in its column).
 *
 * Logarithmic, not linear. With 254 against 1 in the same column a linear
 * split drops almost every cell into bucket 1 and the grid reads uniformly
 * cold — the same failure as today's flat table, inverted.
 *
 * `lowSignalCeiling` stops a column whose max is 1 or 2 from painting its
 * cells at full intensity: a lone job is not a hotspot.
 */
const LOW_SIGNAL_CEILING = 5;
const LOW_SIGNAL_MAX_STEP = 2;

export type IntensityStep = 0 | 1 | 2 | 3 | 4;

export const getIntensityStep = (
  value: number,
  columnMax: number,
  lowSignalCeiling: number = LOW_SIGNAL_CEILING
): IntensityStep => {
  const count = toCount(value);
  // `columnMax` goes through the same guard as `value`: `NaN <= 0` is false, so
  // a bare comparison lets a non-finite max through and yields a NaN step.
  const max = toCount(columnMax);
  if (count <= 0 || max <= 0) return 0;

  const ceiling = max <= 1 ? 1 : Math.ceil((4 * Math.log1p(count)) / Math.log1p(max));
  let step = Math.min(4, Math.max(1, ceiling));

  if (max < lowSignalCeiling) step = Math.min(step, LOW_SIGNAL_MAX_STEP);

  return step as IntensityStep;
};

/**
 * Roll the flat payload up into client groups with subtotals.
 *
 * Rows sharing a `clientCode` are grouped even when they arrive
 * non-contiguously. Groups are ordered by total desc (busiest client first —
 * the old table was alphabetical, which buried the workload), rows within a
 * group likewise.
 */
export const buildActivityMatrix = (
  rows: readonly ServiceActivityResponseDataType[] | undefined
): ActivityMatrixModel => {
  const source = rows ?? [];
  const byClient = new Map<string, ActivityMatrixGroup>();
  const columnMaxima = emptyCounts();
  const totals = emptyCounts();

  source.forEach((row, index) => {
    const clientCode = (row?.clientCode ?? '').trim();
    const serviceTypeName = (row?.serviceTypeName ?? '').trim();

    const counts = emptyCounts();
    for (const column of ACTIVITY_STATUS_COLUMNS) {
      counts[column.field] = toCount(row?.[column.field]);
    }

    const matrixRow: ActivityMatrixRow = {
      // `id` is optional on the payload and was used as the React key by the
      // old table — undefined ids collide. The index tiebreak also keeps
      // duplicate client+type pairs distinct rather than silently merging.
      key: `${clientCode}::${serviceTypeName}::${index}`,
      clientCode,
      serviceTypeName,
      label: deriveRowLabel(row),
      rowHeading: (row?.rowHeading ?? '').trim(),
      counts,
      total: totalOf(counts),
    };

    for (const column of ACTIVITY_STATUS_COLUMNS) {
      const value = counts[column.field];
      totals[column.field] += value;
      if (value > columnMaxima[column.field]) columnMaxima[column.field] = value;
    }

    const groupKey = clientCode || UNGROUPED_CLIENT_LABEL;
    let group = byClient.get(groupKey);
    if (!group) {
      group = {
        key: groupKey,
        clientCode,
        label: clientCode || UNGROUPED_CLIENT_LABEL,
        rows: [],
        subtotals: emptyCounts(),
        total: 0,
      };
      byClient.set(groupKey, group);
    }
    group.rows.push(matrixRow);
    sumCounts(group.subtotals, counts);
    group.total += matrixRow.total;
  });

  const groups = [...byClient.values()];
  for (const group of groups) {
    group.rows.sort((a, b) => b.total - a.total || a.label.localeCompare(b.label));
  }
  groups.sort((a, b) => b.total - a.total || a.label.localeCompare(b.label));

  return {
    groups,
    columnMaxima,
    totals,
    grandTotal: totalOf(totals),
    rowCount: source.length,
  };
};
